import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Button, 
  Modal, 
  Form, 
  Input, 
  message,
  List,
  Tag,
  Space,
  Popconfirm
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  FolderOutlined,
  FolderAddOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { familiesAPI } from '../../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const Families = () => {
  const [isFamilyModalVisible, setIsFamilyModalVisible] = useState(false);
  const [isSubFamilyModalVisible, setIsSubFamilyModalVisible] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [editingSubFamily, setEditingSubFamily] = useState(null);

  const queryClient = useQueryClient();

  const { data: familiesData, isLoading } = useQuery('families', () => familiesAPI.getAll());
  const families = familiesData?.data?.data?.families || [];

  const createFamilyMutation = useMutation(familiesAPI.create, {
    onSuccess: () => {
      message.success('Famille créée avec succès');
      setIsFamilyModalVisible(false);
      setEditingFamily(null);
      queryClient.invalidateQueries('families');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Erreur lors de la création');
    }
  });

  const updateFamilyMutation = useMutation(
    ({ id, data }) => familiesAPI.update(id, data),
    {
      onSuccess: () => {
        message.success('Famille mise à jour avec succès');
        setIsFamilyModalVisible(false);
        setEditingFamily(null);
        queryClient.invalidateQueries('families');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Erreur lors de la mise à jour');
      }
    }
  );

  const deleteFamilyMutation = useMutation(familiesAPI.delete, {
    onSuccess: () => {
      message.success('Famille supprimée avec succès');
      queryClient.invalidateQueries('families');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  });

  const createSubFamilyMutation = useMutation(
    ({ familyId, data }) => familiesAPI.createSubFamily(familyId, data),
    {
      onSuccess: () => {
        message.success('Sous-famille créée avec succès');
        setIsSubFamilyModalVisible(false);
        setEditingSubFamily(null);
        queryClient.invalidateQueries('families');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Erreur lors de la création');
      }
    }
  );

  const updateSubFamilyMutation = useMutation(
    ({ id, data }) => familiesAPI.updateSubFamily(id, data),
    {
      onSuccess: () => {
        message.success('Sous-famille mise à jour avec succès');
        setIsSubFamilyModalVisible(false);
        setEditingSubFamily(null);
        queryClient.invalidateQueries('families');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Erreur lors de la mise à jour');
      }
    }
  );

  const deleteSubFamilyMutation = useMutation(familiesAPI.deleteSubFamily, {
    onSuccess: () => {
      message.success('Sous-famille supprimée avec succès');
      queryClient.invalidateQueries('families');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  });

  const handleFamilySubmit = (values) => {
    if (editingFamily) {
      updateFamilyMutation.mutate({ id: editingFamily.id, data: values });
    } else {
      createFamilyMutation.mutate(values);
    }
  };

  const handleSubFamilySubmit = (values) => {
    if (editingSubFamily) {
      updateSubFamilyMutation.mutate({ id: editingSubFamily.id, data: values });
    } else {
      createSubFamilyMutation.mutate({ familyId: selectedFamily.id, data: values });
    }
  };

  const renderFamilyCard = (family) => (
    <Card
      key={family.id}
      title={
        <Space>
          <FolderOutlined style={{ color: family.color }} />
          {family.name}
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => {
              setEditingFamily(family);
              setIsFamilyModalVisible(true);
            }}
          />
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cette famille?"
            onConfirm={() => deleteFamilyMutation.mutate(family.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
            />
          </Popconfirm>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Text type="secondary">{family.description}</Text>
      
      <div style={{ marginTop: 16 }}>
        <Space style={{ marginBottom: 8 }}>
          <Text strong>Sous-familles:</Text>
          <Button
            icon={<FolderAddOutlined />}
            size="small"
            onClick={() => {
              setSelectedFamily(family);
              setIsSubFamilyModalVisible(true);
            }}
          >
            Ajouter
          </Button>
        </Space>
        
        <List
          size="small"
          dataSource={family.subFamilies || []}
          renderItem={(subFamily) => (
            <List.Item
              actions={[
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => {
                    setEditingSubFamily(subFamily);
                    setSelectedFamily(family);
                    setIsSubFamilyModalVisible(true);
                  }}
                />,
                <Popconfirm
                  title="Êtes-vous sûr de vouloir supprimer cette sous-famille?"
                  onConfirm={() => deleteSubFamilyMutation.mutate(subFamily.id)}
                  okText="Oui"
                  cancelText="Non"
                >
                  <Button
                    icon={<DeleteOutlined />}
                    size="small"
                    danger
                  />
                </Popconfirm>
              ]}
            >
              <Text>{subFamily.name}</Text>
            </List.Item>
          )}
          locale={{ emptyText: 'Aucune sous-famille' }}
        />
      </div>
    </Card>
  );

  return (
    <div>
      <div className="page-header">
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2}>Familles et Sous-familles</Title>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingFamily(null);
                setIsFamilyModalVisible(true);
              }}
            >
              Ajouter une famille
            </Button>
          </Col>
        </Row>
      </div>

      <Row gutter={[16, 16]}>
        {families.map(renderFamilyCard)}
      </Row>

      <Modal
        title={editingFamily ? 'Modifier la famille' : 'Ajouter une famille'}
        open={isFamilyModalVisible}
        onCancel={() => {
          setIsFamilyModalVisible(false);
          setEditingFamily(null);
        }}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={handleFamilySubmit}
          initialValues={editingFamily}
        >
          <Form.Item
            name="name"
            label="Nom de la famille"
            rules={[{ required: true, message: 'Veuillez saisir le nom' }]}
          >
            <Input placeholder="Ex: Clients" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea placeholder="Description de la famille" rows={3} />
          </Form.Item>

          <Form.Item
            name="color"
            label="Couleur"
          >
            <Input type="color" />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={createFamilyMutation.isLoading || updateFamilyMutation.isLoading}
              block
            >
              {editingFamily ? 'Mettre à jour' : 'Créer'} la famille
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingSubFamily ? 'Modifier la sous-famille' : 'Ajouter une sous-famille'}
        open={isSubFamilyModalVisible}
        onCancel={() => {
          setIsSubFamilyModalVisible(false);
          setEditingSubFamily(null);
          setSelectedFamily(null);
        }}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={handleSubFamilySubmit}
          initialValues={editingSubFamily}
        >
          <Form.Item>
            <Text strong>Famille:</Text> {selectedFamily?.name}
          </Form.Item>

          <Form.Item
            name="name"
            label="Nom de la sous-famille"
            rules={[{ required: true, message: 'Veuillez saisir le nom' }]}
          >
            <Input placeholder="Ex: Clients Premium" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea placeholder="Description de la sous-famille" rows={3} />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={createSubFamilyMutation.isLoading || updateSubFamilyMutation.isLoading}
              block
            >
              {editingSubFamily ? 'Mettre à jour' : 'Créer'} la sous-famille
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Families;
